<script setup>
import { defineProps, defineEmits } from 'vue';

const props = defineProps({
    showModal: {
        type: Boolean,
        required: true
    },
    actionType: {
        type: String,
        default: 'access this feature'
    }
});

const emit = defineEmits(['update:showModal', 'login', 'register']);

const handleClose = () => {
    emit('update:showModal', false);
};

const handleLogin = () => {
    emit('login');
    handleClose();
};

const handleRegister = () => {
    emit('register');
    handleClose();
};

const handleClickOutside = (event) => {
    if (event.target.id === 'auth-prompt-modal') {
        handleClose();
    }
};
</script>

<template>
    <Teleport to="body">
        <div
            v-if="showModal"
            id="auth-prompt-modal"
            class="z-50 overflow-hidden inset-0 m-auto flex justify-center items-center bg-echoes-background-black/50 backdrop-blur-sm"
            :class="showModal ? 'fixed' : 'hidden'"
            tabindex="-1"
            @click="handleClickOutside"
        >
            <div class="relative w-full h-auto max-w-md my-auto p-4 animate-slideIn">
                <div class="relative bg-echoes-background-gray rounded-[21px] shadow-xl animate-slideIn overflow-hidden border border-echoes-border-light">
                    <!-- Header -->
                    <div class="flex justify-between border-b border-echoes-border-light items-center px-6 py-4">
                        <h5 class="text-lg text-white font-bold">Sign in to Continue</h5>
                        <button 
                            type="button"
                            @click="handleClose"
                            class="text-white bg-transparent hover:bg-white/10 hover:text-red-600 transition-colors duration-300 ease-in-out rounded-lg text-sm p-1.5 ms-auto inline-flex items-center"
                        >
                            <span class="sr-only">Close modal</span>
                            <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- Body -->
                    <div class="px-6 py-8 space-y-6">
                        <!-- Icon -->
                        <div class="flex justify-center">
                            <div class="w-16 h-16 rounded-full bg-echoes-background-black/70 flex items-center justify-center">
                                <i class="mdi mdi-account-lock text-white text-3xl"></i>
                            </div>
                        </div>

                        <!-- Message -->
                        <div class="text-center space-y-2">
                            <p class="text-white text-base font-medium">
                                Sign in to {{ actionType }}
                            </p>
                            <p class="text-white/60 text-sm">
                                You need to sign in or create an account to access this feature.
                            </p>
                        </div>

                        <!-- Action Buttons -->
                        <div class="space-y-3">
                            <button
                                @click="handleLogin"
                                class="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-[13px] font-semibold text-sm transition-all duration-200 bg-gradient-to-r text-sm 2xl:text-base from-green-500 to-sky-800 text-white hover:from-green-600 hover:via-green-500 hover:to-sky-700 cursor-pointer"
                            >
                                <i class="mdi mdi-login text-lg"></i>
                                Sign In
                            </button>

                            <button
                                @click="handleRegister"
                                class="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-[13px] font-semibold text-sm transition-all duration-200 bg-gradient-to-r text-sm 2xl:text-base from-blue-500 to-violet-800 text-white hover:from-blue-600 hover:via-blue-500 hover:to-violet-700 cursor-pointer"
                            >
                                <i class="mdi mdi-account-plus text-lg"></i>
                                Register
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="postcss">
@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.animate-slideIn {
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style>

